import Controller from "sap/ui/core/mvc/Controller";
import VamosComponent from "vamos/cockpit/Component";
import JSONModel from "sap/ui/model/json/JSONModel";
import Model from "sap/ui/model/Model";
import Component from "sap/ui/core/Component";
import TabContainer, { TabContainer$ItemCloseEvent, TabContainer$ItemCloseEventParameters } from "sap/m/TabContainer";
import Button, { Button$PressEvent } from "sap/m/Button";
import TabContainerItem from "sap/m/TabContainerItem";
import Table from "sap/ui/table/Table";
import Binding, { Binding$DataReceivedEvent, Binding$DataRequestedEvent } from "sap/ui/model/Binding";
import Context from "sap/ui/model/Context";
import DialogService from "vamos/cockpit/base/services/DialogService";
import UI5Element from "sap/ui/core/Element";
import Element from "sap/ui/core/Element";
import ManagedObject from "sap/ui/base/ManagedObject";
import ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";
import V4Context from "sap/ui/model/odata/v4/Context";
import { showSuccess } from "vamos/cockpit/base/handler/MessageToastHandler";
import NavigationHandler, { NavConfig } from "vamos/cockpit/base/handler/NavigationHandler";
import ODataModel from "sap/ui/model/odata/v4/ODataModel";
import InputBase from "sap/m/InputBase";
import { ValueState } from "sap/ui/core/library";
import Router from "sap/f/routing/Router";
import getText from "vamos/cockpit/base/handler/Text";
import { default as ResourceBundle } from "sap/base/i18n/ResourceBundle";
import ResourceModel from "sap/ui/model/resource/ResourceModel";
import MultiSelectionPlugin from "sap/ui/table/plugins/MultiSelectionPlugin";
import { SelectionMode } from "sap/ui/table/library";
import { NavData } from "vamos/cockpit/base/definitions/NavData";
import BusyIndicator from "sap/ui/core/BusyIndicator";
import MessageBox from "sap/m/MessageBox";
import Row from "sap/ui/table/Row";
import syncStyleClass from "sap/ui/core/syncStyleClass";

/**
 * @namespace vamos.cockpit.base.controller.type
 * */
export default class BaseController extends Controller {

    protected tables: Table[];

    protected tableModel: JSONModel;

    protected navigationHandler: NavigationHandler;

    protected readonly _sEmptyHistoryRoute?: string = null;

    private itemClosed: Event<TabContainer$ItemCloseEventParameters, TabContainer>;

    protected runInit: boolean;

    protected viewModel = new JSONModel({});

    protected oDefaultVariantTableContext = {};

    protected _bApplyingP13nState = false;

    public onInit(): void {
        this.setModel(this.viewModel, "view");
        this.navigationHandler = new NavigationHandler(this);
    }

    public setModel(model: JSONModel, name?: string): void {
        this.getView().setModel(model, name);
    }

    public getModel(name?: string): Model {
        return this.getView().getModel(name) || this.getComponent().getModel(name);
    }

    public getComponent(): VamosComponent {
        return Component.getOwnerComponentFor(this.getView()) as VamosComponent;
    }

    public getTabContainer(): TabContainer {
        return this.getComponent().getTabContainer();
    }

    public getSelectedTabContainerItem(): TabContainerItem {
        const oTabContainer = this.getTabContainer();
        return oTabContainer.getItems().find((oItem: TabContainerItem) => {
            return oItem.getId() === oTabContainer.getSelectedItem();
        });
    }

    public getTabContainerItemByRoute(route: string) {
        return this.getTabContainer()
            .getItems()
            .find((oItem) => {
                return route.includes(oItem.data("routeHash")) || oItem.data("routeHash")?.includes(route);
            });
    }

    public attachItemCloseToContainer(): void {
        // after itemclosed rerun init
        const oTabContainer = this.getTabContainer();
        // not yet available on direct page reload
        if (!oTabContainer) {
            return;
        }
        const oItem = this.getSelectedTabContainerItem();
        if (this.itemClosed) {
            oTabContainer.detachItemClose(this.itemClosed);
        }
        this.itemClosed = (evt: TabContainer$ItemCloseEvent) => {
            if (evt.getParameter("item") !== oItem) {
                return;
            }

            this.runInit = true;
        };
        oTabContainer.attachItemClose(this.itemClosed);
    }

    public onOpenFragmentPopup(event: Button$PressEvent): void {
        const source: Button = event.getSource();
        const name: string = source.data("fragmentName");
        const contextCallbackName: string = source.data("contextCallback");
        const postLoadCallbackName: string = source.data("postCallback");
        const entitySet: string = source.data("entitySet");
        const title: string = source.data("fragmentTitle");
        const initialData: any = source.data("initialValues");
        const isEditDialog: boolean = source.data("editDialog") === "true";
        // @ts-ignore
        const fnPostLoadCallback: Function = postLoadCallbackName ? this[postLoadCallbackName] : undefined;
        let table: Table;
        let binding: Binding;

        let odataContext = null;
        if (source.data("applySourceBinding")) {
            odataContext = source.getBindingContext();
        } else if (entitySet) {
            table = this.findParentControl("sap.ui.table.Table", source) as Table;
            binding = table.getBinding("rows");
            odataContext = this.createModelEntry(entitySet, initialData, binding);
        } else if (contextCallbackName) {
            // @ts-ignore
            if (this[contextCallbackName]) {
                //@ts-ignore
                odataContext = this[contextCallbackName].call(this, event);
            }
        }
        const contextPromise: Promise<Context> = Promise.resolve(odataContext);
        contextPromise.then((context: Context) => {
            DialogService.openDialog.call(this, name, context, fnPostLoadCallback, title, isEditDialog);
        });
    }

    public findParentControl(classNames: string | string[], source?: UI5Element) {
        let node: ManagedObject = source ? source.getParent() : this.getView().getParent();
        classNames = Array.isArray(classNames) ? classNames : [classNames];

        while (node !== null && classNames.indexOf(node.getMetadata().getName()) === -1) {
            node = node.getParent();
        }

        return node;
    }

    public createModelEntry(
        entitySet: string,
        initialParams: any = {},
        listBinding?: Binding,
        groupId: string = "create",
    ): Context {
        const sPath = entitySet.startsWith("/") ? entitySet : "/" + this.getEntitySetName(entitySet);
        const binding: ODataListBinding = this.getModel().bindList(sPath, undefined, undefined, undefined, {
            $$updateGroupId: groupId,
        }) as ODataListBinding;
        binding.attachEvent("createCompleted", undefined, (): void => {
            if (listBinding && listBinding instanceof ODataListBinding) {
                listBinding.resetChanges();
                listBinding.refresh();
            }
        });
        const newContext: V4Context = binding.create(initialParams, /*skipRefresh*/ true, undefined, false);

        // catch promise, if the context gets deleted before send to the server
        newContext.created().then(
            () => {
                if (listBinding instanceof ODataListBinding) {
                    listBinding.resetChanges();
                    listBinding.refresh();
                } else if (listBinding) {
                    //add Object to jsonmodel
                    const model: JSONModel = listBinding.getModel() as JSONModel;
                    const sPath = listBinding.getPath();
                    const aObjects = model.getProperty(sPath);
                    aObjects.push(newContext.getObject());
                    model.setProperty(sPath, aObjects);
                }
            },
            () => {
            },
        );
        return newContext;
    }

    public onClosePopup(oEvent: any, oTarget?: any) {
        let oNode = oTarget ? oTarget : oEvent.getSource();

        while (oNode !== null && !oNode.close) {
            oNode = oNode.getParent();
        }
        if (oNode && oNode.close) {
            oNode.close();
        }
    }

    public navTo(routeName: string, config: NavConfig, ignoreHistory = false): void {
        this.navigationHandler.navigateTo(routeName, config, ignoreHistory);
    }

    public resetInputValueState(parentElement: UI5Element) {
        parentElement.findElements(true).forEach((oChildElement: UI5Element) => {
            if (oChildElement instanceof InputBase) {
                oChildElement.setValueState(ValueState.None);
            }
        });
    }

    public onNavBack() {
        this.navigationHandler.onNavBack();
    }

    public getKeyPredicateFromEntitySet(entitySet: string, guid: string): string;
    public getKeyPredicateFromEntitySet(entitySet: string, entity: { Guid: string }): string;
    public getKeyPredicateFromEntitySet(entitySet: string, arg: string | { Guid: string }): string {
        const entity = typeof arg === "string" ? { Guid: arg } : arg;
        const oModel = this.getModel() as ODataModel;
        const sEntitySetName = this.getEntitySetName("/" + entitySet);
        return sEntitySetName + oModel.getKeyPredicate(sEntitySetName, entity);
    }

    public getEntitySetName(entityTypeName: string): string {
        if (
            entityTypeName.charAt(entityTypeName.length - 1) === "y" &&
            entityTypeName[entityTypeName.length - 2] !== "a" &&
            entityTypeName.charAt(entityTypeName.length - 2) !== "e" &&
            entityTypeName.charAt(entityTypeName.length - 2) !== "i" &&
            entityTypeName.charAt(entityTypeName.length - 2) !== "o" &&
            entityTypeName.charAt(entityTypeName.length - 2) !== "u"
        ) {
            return entityTypeName.substring(0, entityTypeName.length - 1) + "ies";
        }
        return entityTypeName + "s";
    }

    /** @deprecated Use MessageToastHandler$showSuccess */
    public showSuccessMessage(message: string): void {
        showSuccess(message);
    }

    /////////////////////////////////////
    public clone(oObject: object): object {
        return JSON.parse(JSON.stringify(oObject));
    }

    public handleTabHistoryFromListViews(routeName: string): void {
        const oTabItem = this.getTabContainerItemByRoute(routeName);
        const aHistory = oTabItem?.data("history") as string[] || [];
        if (aHistory.length > 0 && !(aHistory.length === 1 && aHistory[0] === routeName)) {
            aHistory.pop();
        }
        oTabItem?.data("history", aHistory);
    }

    public getResourceBundle(): ResourceBundle {
        const model: ResourceModel = this.getOwnerComponent().getModel("i18n") as ResourceModel;
        return model.getResourceBundle() as ResourceBundle;
    }

    public getHash(): string {
        return this.getRouter().getHashChanger().getHash();
    }

    // @Todo change Refreshing
    public getViewTablesAndRefresh(): void {
        if (!this.tables) {
            this.tables = this.getView()
                .findElements(true)
                .filter((oElements) => {
                    return oElements instanceof Table;
                });
            this.tables.forEach((oTable: Table) => {
                const oBinding = oTable.getBinding("rows");
                if (!oBinding) {
                    return;
                }
                oBinding.attachDataRequested((oEvent: Binding$DataRequestedEvent) => {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error
                    oEvent.getSource().refreshing = true;

                });
                oBinding.attachDataReceived((oEvent: Binding$DataReceivedEvent) => {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error
                    oEvent.getSource().refreshing = false;
                });
            });
            return;
        }
        this.tables.forEach((oTable) => {
            const oBinding = oTable.getBinding("rows");
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            if (oBinding && !oBinding.refreshing) {
                oBinding?.refresh();
            }
        });
    }

    public getEmptyHistoryRoute(): null | string {
        return this._sEmptyHistoryRoute;
    }

    public getText(sKey: string, aArgs: any[] = undefined) {
        return getText(sKey, aArgs);
    }

    public getRouter(): Router {
        const component = this.getOwnerComponent() as VamosComponent;
        return <Router>component.getRouter();
    }

    public getViewModel(): JSONModel {
        return this.viewModel;
    }

    public getTableModel(): JSONModel {
        return this.tableModel;
    }

    public getDefaultVariantTableContext(): any {
        return this.oDefaultVariantTableContext;
    }

    public getSelectedTableIndices(oTable: Table): int[] {
        let arSelectedIndices: int[] = [];
        try {
            if (oTable) {
                const dependents = oTable.getDependents();
                let isMultiSelection = false;
                if (dependents != null && dependents.length > 0) {
                    for (let i = 0; i < dependents.length; i++) {
                        if (dependents[i].isA("sap.ui.table.plugins.MultiSelectionPlugin")) {
                            const multiSelectionPlugin = dependents[i] as MultiSelectionPlugin;
                            arSelectedIndices = multiSelectionPlugin.getSelectedIndices();
                            isMultiSelection = true;
                            break;
                        }
                    }
                }
                if (!isMultiSelection) {
                    arSelectedIndices = oTable.getSelectedIndices();
                }
            }
        } catch (e) {
            console.error(e);
        }
        return arSelectedIndices;
    }

    public getSelectedItemContext(event: Button$PressEvent): Context {
        const table = event.getSource().isA("sap.ui.table.Table")
                ? event.getSource() as unknown as Table
                : this.getTableByButtonEvent(event);
        const selectedIndices = this.getSelectedTableIndices(table);
        return table ? table.getContextByIndex(selectedIndices[0]) : this.getView().getBindingContext();
    }

    getSelectedTableContext(oTable: Table, bReturnObject = false) {
        const aSelectedIndices = this.getSelectedTableIndices(oTable);
        const bMultiSelect = oTable.getSelectionMode() === SelectionMode.MultiToggle;
        const arData = aSelectedIndices.map((iIndex) => {
            const oContext = oTable.getContextByIndex(iIndex);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return bReturnObject ? oContext.getObject() : oContext;
        });
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return bMultiSelect ? arData : arData[0];
    }

    public applyColumnStates(sTableId: string) {
        const oStates = this.tableModel.getProperty(`/${sTableId}/states`);
        const oTable = this.getView().byId(sTableId);
        if (oTable.getPersoButton) {
            this._bApplyingP13nState = true;
            oTable.getPersoButton().applyState(oStates);
            this._bApplyingP13nState = false;
        }
    }

    public async submitBatch(groupId?: string) {
        const model: ODataModel = this.getModel() as ODataModel;
        groupId = groupId || model.getUpdateGroupId();
        await model.submitBatch(groupId);
        if (!model.hasPendingChanges(groupId)) {
            return Promise.resolve({ success: true });
        }
        return Promise.reject(new Error("Batch not submitted"));
    }

    public async onEditSave() {
        try {
            await this.submitBatch();
            this.navigationHandler.onNavBack();
        } catch (e) {
            console.error(e);
        }
    }

    public onNavByButton(event: Button$PressEvent) {
        const button = event.getSource();
        const navData: NavData = button.data("navData") as NavData;
        const tableId = button.getAriaLabelledBy()[0];
        if (!tableId) {
            console.error("No tableId found");
        }
        const table: Table = this.getView().byId(tableId) as Table;
        if (!table) {
            console.error("No table found");
        }
        const indices: number[] = this.getSelectedTableIndices(table);
        const context = table.getContextByIndex(indices[0]) as V4Context;
        const path = this.getKeyPredicateFromEntitySet(navData.entitySet, {
            Guid: context.getProperty(navData.referencePath) as string,
        });
        this.setRouteDisplayedContext(navData.routeName, context);
        this.navigationHandler.navigateTo(navData.routeName, { path: path }, false);

    }

    public setRouteDisplayedContext(routeName: string, context: V4Context): void {
        const oAppModel = this.getModel("app") as JSONModel;
        if (!oAppModel.getProperty("/appStateData")) {
            oAppModel.setProperty("/appStateData", {});
        }
        oAppModel.setProperty(`/appStateData/${routeName}`, context);
    }

    public getTableByButtonEvent(event: Button$PressEvent): Table {
        const oButton = event.getSource();
        const arAriaLabelledBy: string[] = oButton.getAriaLabelledBy();
        if (arAriaLabelledBy.length < 1) {
            console.error("Please define ariaLabelledby");
            return;
        }
        return this.byId(arAriaLabelledBy[0]) as Table || this.getComponent().byId(arAriaLabelledBy[0]) as Table;
    }

    public onDeleteListItems(event: Button$PressEvent) {
        BusyIndicator.show();

        const table = this.getTableByButtonEvent(event);
        const selectedIndices: number[] = this.getSelectedTableIndices(table).reverse();
        const oBinding = table.getBinding("rows");
        if (oBinding.isA("sap.ui.model.json.JSONListBinding")) {
            const model = oBinding.getModel() as JSONModel;
            const path = oBinding.getPath();
            let items: any = model.getProperty(path);
            const deletedGuids: string[] = [];
            selectedIndices.forEach((iIndex) => {
                const context: Context = table.getContextByIndex(iIndex);
                deletedGuids.push(context.getProperty("Guid"));
                // you have to define an property and context is the loaded context
                void (context.getProperty("context") as V4Context).delete("create");
            });
            items = items.filter((item: any) => !deletedGuids.includes(item.Guid));
            model.setProperty(path, items);
        } else {
            selectedIndices.forEach((iIndex) => {
                const context = table.getContextByIndex(iIndex) as V4Context;
                //hanndle JSONBinding

                void context.delete("create");
            });
        }
        this.submitBatch("create")
            .then(
                () => {
                    if (this.afterDeleteListItemsSuccessfully) {
                        this.afterDeleteListItemsSuccessfully(table);
                    }
                    showSuccess(this.getText("EntryDeleted"));
                    oBinding.refresh();
                },
                () => {
                    console.error("deleteFailed");
                },
            )
            .finally(() => {
                BusyIndicator.hide();
            });
    }

    public afterDeleteListItemsSuccessfully(table: Table) {

    }

    public onDeleteWithConfirmation(event: Button$PressEvent) {
        MessageBox.show(this.getText("DeleteEntryText"), {
            icon: MessageBox.Icon.INFORMATION,
            title: this.getText("Delete"),
            actions: [MessageBox.Action.OK, MessageBox.Action.CANCEL],
            onClose: (action: string) => {
                if (action === MessageBox.Action.OK.toString()) {
                    this.onDeleteListItems(event);
                }
            },
        });
    }

    public actionWithUserConfirmation(title: string, message: string, fnAfterConfirmed: (args?: unknown) => void, type?: string, args?: unknown) {
        MessageBox.show(message, {
            icon: type ? MessageBox.Icon[type.toUpperCase() as keyof typeof MessageBox.Icon] : MessageBox.Icon.WARNING,
            title: title,
            actions: [MessageBox.Action.OK, MessageBox.Action.CANCEL],
            onClose: (action: string) => {
                if (action === MessageBox.Action.OK.toString()) {
                    fnAfterConfirmed.call(this, args);
                }
            },
        });
    }

    public onNavByViewData(event: Button$PressEvent) {
        const button = event.getSource();
        const navData: NavData = button.data("navData") as NavData;
        const context = this.getView().getBindingContext() as V4Context;
        const path = this.getKeyPredicateFromEntitySet(navData.entitySet, {
            Guid: context.getProperty(navData.referencePath) as string,
        });
        this.setRouteDisplayedContext(navData.routeName, context);
        this.navigationHandler.navigateTo(navData.routeName, { path: path }, false);
    }

    public attachDoubleClickEvent(controller: BaseController, table: Table) {
        const functionName = table.data("dblClick") as string;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        if (functionName && controller[functionName] && typeof controller[functionName] === "function") {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            const fn = controller[functionName] as () => void;
            table.addEventDelegate({
                onAfterRendering() {
                    const rows: Row[] = (this as Table).getRows();
                    rows.forEach((row) => {
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                        row.$().on("dblclick", fn.bind(controller));
                    });
                },
            }, table);
        }
    }

    public async onRowDblClickNav(event: MouseEvent) {
        const row = Element.closestTo(event.currentTarget) as Row;
        const table = row.getTable() as Table;

        const multiSelectionPlugin = this.getTableHasMultiSectionPlugin(table);

        if(multiSelectionPlugin) {
            await multiSelectionPlugin.addSelectionInterval(row.getIndex(), row.getIndex());
        } else {
            table.addSelectionInterval(row.getIndex(), row.getIndex());
        }

        const navData: NavData = table.data("navData") as NavData;
        const context = row.getBindingContext() as V4Context;
        if (!context) {
            return;
        }
        const sPath = this.getKeyPredicateFromEntitySet(navData.entitySet, {
            Guid: context.getProperty(navData.referencePath) as string,
        });
        this.setRouteDisplayedContext(navData.routeName, context);
        this.navTo(navData.routeName, { path: sPath });
    }

    private getTableHasMultiSectionPlugin(table: Table) : MultiSelectionPlugin {
        let multiSelectionPlugin = null;
        const depends = table.getDependents();
        if (depends != null && depends.length > 0) {
            for (let i = 0; i < depends.length; i++) {
                if (depends[i].isA("sap.ui.table.plugins.MultiSelectionPlugin")) {
                    multiSelectionPlugin = depends[i];
                    break;
                }
            }
        }
        return multiSelectionPlugin as MultiSelectionPlugin;
    }


    public onNavTo(oEvent: Button$PressEvent) {
        const oElement = oEvent.getSource();
        const oNavData = oElement.data("navData") as NavData;
        const oContext = oElement.getBindingContext();
        if (oNavData) {
            const sPath = this.getKeyPredicateFromEntitySet(oNavData.entitySet, {
                Guid: oContext.getProperty(oNavData.referencePath) as string,
            });
            this.navTo(oNavData.routeName, { path: sPath });
        } else {
            const sRoute = oElement.data("routeName") as string;
            this.setSelectedTab(sRoute);
        }
    }

    protected setSelectedTab(sKey: string) {
        const oTabContainerItem = this.getTabContainerItemByKey(sKey);
        !!oTabContainerItem
            ? this.getTabContainer().setSelectedItem(oTabContainerItem)
            : this.navTo(sKey, {});
    }

    protected getTabContainerItemByKey(sKey: string) {
        const oTabContainer = this.getTabContainer();
        const arItems = oTabContainer.getItems();
        return arItems.find((oItem) => oItem.getKey() === sKey);
    }

    public refreshWholeModel(): void {
        const model = this.getModel() as ODataModel & { removeBinding(binding: Binding): void };
        model.getAllBindings()
            .forEach((binding: Binding) => {
                if (!this.isRootBinding(binding)) {
                    return;
                }
                if (binding.isA<ODataListBinding>("sap.ui.model.odata.v4.ODataListBinding")
                    && !(binding as ODataListBinding & { aContexts?: V4Context[] }).aContexts) {
                    model.removeBinding(binding);
                } else {
                    binding.refresh(undefined);
                }
            });
    }

    private isRootBinding(binding: Binding): boolean {
        const context = binding.getContext();
        return !binding.isRelative() || (!!context && !context.isA("sap.ui.model.odata.v4.Context"));
    }

    public getXmlFragment(sId: string) {
        let sName = sId;
        const sPrefix = sName.substring(0, 1);
        const oView = this.getView();


        if (sPrefix === ".") {
            const sViewName = oView.getViewName(),
                iLastDotPos = sViewName.lastIndexOf("."),
                sPackage = sViewName.substring(0, iLastDotPos);
            sName = sPackage + sName;
        } else if (sPrefix === "/") {
            const sComponentName = this.getComponent().getMetadata().getComponentName();
            sName = sComponentName + ".view" + sName;
        }

        const sFragmentId = sId;
        const sCacheId = sFragmentId + "--" + sName;

        let fragment = this.getComponent().getFragment(sCacheId);


        if (!fragment) {
            // instantiate and cache fragment
            this.getComponent().runAsOwner(() => {
                fragment = sap.ui.xmlfragment(sFragmentId, sName, this);
            });
            this.getComponent().setFragment(sCacheId, fragment);
        }

        // (re-)add fragment dependency to the calling view so it inherits this
        // view's models (e.g. "view") instead of staying attached to whichever
        // view first created/cached the fragment
        oView.addDependent(fragment);

        // toggle compact style
        syncStyleClass("sapUiSizeCompact", oView, fragment);

        return fragment;
    }
    public async onSubmitMDCField(event: FieldBase$SubmitEvent) {
        if (!event.isA("sap.ui.base.Event")) {
            throw new Error("wrong event parameters");
        }

        const field: Field = event.getSource();
        const sId = field.getValueHelp();
        const oVH = this.byId(sId) as ValueHelp;
        const oDelegate = oVH.getControlDelegate() as JSONValueHelpDelegate;
        // in case it is already loaded
        const value = await event.getParameter("promise");
        const contexts = oDelegate.filterCurrentContext(oVH, value);
        if (contexts && contexts.length === 1) {
            const context = contexts[0];
            const properties = oDelegate.getBoundProperties(oVH);
            const sValue = context.getProperty(properties.key);
            const eventParams = { valid: true, value: sValue };
            oDelegate.setSelectedContext(oVH, context);
            field.setValue(context.getProperty(properties.key));
            field.setAdditionalValue(context.getProperty(properties.description));
            field.fireChange(eventParams);
        }
    }
    public async handleValueHelpUpdateStarted(oEvent: ListBase$UpdateStartedEvent):Promise<void>{
        if (oEvent.getParameter("reason") !== "Growing") {
            return;
        }
        const oTable = oEvent.getSource();
        const oVH = this.findParentControl("sap.ui.mdc.ValueHelp", oTable);
        oTable.invalidate();

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const oListBinding = oVH._oListBinding as ODataListBinding;
        let iActual;
        if (!oListBinding) {
            return;
        }
        iActual = oTable.getItems().length;
        if (iActual >= oListBinding.getCount()) {
            return;
        }

        oTable.setBusy(true);
        if (iActual < oListBinding.getCount()) {
           await oListBinding.requestContexts(iActual, iActual + 100);
           oTable.getModel("ValueHelp").refresh(true);
        }

    }
    handleValueHelpOpened(event:ValueHelp$OpenedEvent) {
        const vh = event.getSource();
        const delegate:JSONFilterFieldDelegate = vh.getControlDelegate() as JSONFilterFieldDelegate;
        const table = delegate.getTable(vh);

        const model = table.getBinding("items").getModel();
        if (model instanceof JSONModel) {
            const oInfo = table.getBindingInfo("items");
            if (oInfo.filters) {
                oInfo.filters = table.getBinding("items").getFilters("Application");
            }
            table.removeAllItems();
            table.bindAggregation("items", oInfo);
        }

        table.refreshAggregation("items");
    }
    handleValueHelpUpdateFinished(event){
        const table = event.getSource();
        let items = table.getItems();
        items = items.filter((item:any) => {
            const text = item.getCells()?.[0].getText();
            return !text;
        });

        if(items.length === 0) return;

        for(let i = 0; i < items.length; i++) {
            table.removeItem(items[i]);
        }
    }

}